/* =============================================================
   The navigation stack, and how it survives a URL.

   A frame is one thing being looked at: { type, id, tab }.
   The stack is the trail that got you there, oldest first:

     [ {student, 123, batches}, {batch, 456} ]

   Kept in the query string rather than in component state, so a
   drilled-in view can be pasted to someone else and the browser's
   own back button pops a level. That is the whole reason this
   file exists -- encoding a stack into a param is a small amount
   of fiddly work, and retrofitting it later would mean unpicking
   every caller.
   ============================================================= */

// The query parameter the stack lives in.
export const VIEW_PARAM = 'view';

/* Frames are separated by "," and their fields by ":".

   Both characters are legal in a query value and neither appears
   in the things being encoded: types are lowercase words this
   file controls, ids are Mongo ObjectIds (24 hex characters), and
   tabs are lowercase words declared by a view. Anything that does
   not match that shape is dropped rather than guessed at -- a
   hand-mangled URL should open nothing, not something arbitrary. */
const FRAME_SEP = ',';
const FIELD_SEP = ':';

const TYPE_RE = /^[a-z][a-z0-9-]{0,31}$/;
const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const TAB_RE = /^[a-z][a-z0-9-]{0,31}$/;

/** One frame -> "type:id" or "type:id:tab". */
const encodeFrame = (frame) => {
  if (!frame || !frame.type || !frame.id) return null;
  const base = `${frame.type}${FIELD_SEP}${frame.id}`;
  return frame.tab ? `${base}${FIELD_SEP}${frame.tab}` : base;
};

/** "type:id:tab" -> a frame, or null if it is not shaped like one. */
const decodeFrame = (raw) => {
  const parts = String(raw || '').split(FIELD_SEP);
  if (parts.length < 2 || parts.length > 3) return null;

  const [type, id, tab] = parts;
  if (!TYPE_RE.test(type) || !ID_RE.test(id)) return null;
  if (tab !== undefined && !TAB_RE.test(tab)) return null;

  return tab ? { type, id, tab } : { type, id };
};

/** The whole stack as one parameter value. Empty stack -> null. */
export const encodeStack = (stack) => {
  const frames = (stack || []).map(encodeFrame).filter(Boolean);
  return frames.length ? frames.join(FRAME_SEP) : null;
};

/**
 * A parameter value back into a stack. Unreadable frames are
 * dropped; if that leaves nothing, the result is an empty stack
 * and the host simply shows no modal.
 */
export const decodeStack = (raw) => {
  if (!raw) return [];
  return String(raw)
    .split(FRAME_SEP)
    .map(decodeFrame)
    .filter(Boolean)
    // A stack deeper than this is a loop or a mangled URL, not a
    // person clicking. Keep the trail that got them here and drop
    // the rest rather than rendering hundreds of frames.
    .slice(0, 12);
};

/** Read the stack out of a location's search string. */
export const stackFromSearch = (search) =>
  decodeStack(new URLSearchParams(search || '').get(VIEW_PARAM));

/**
 * Put a stack into a search string, leaving every other parameter
 * where it was -- list pages keep their own filters in the URL and
 * opening a popup must not disturb them.
 */
export const searchWithStack = (search, stack) => {
  const params = new URLSearchParams(search || '');
  const encoded = encodeStack(stack);
  if (encoded) params.set(VIEW_PARAM, encoded);
  else params.delete(VIEW_PARAM);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

/** Do two stacks describe the same place? Used to avoid redundant history entries. */
export const sameStack = (a, b) => encodeStack(a) === encodeStack(b);
